import React from 'react'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { subscribeWithSelector } from 'zustand/middleware'
import type { StateCreator } from 'zustand'
import { Message } from 'rsuite'
import { createBaseSlice } from '../../comp_reusable/report_page_universal/stores/baseReportStore'
import type { ArtifactReport } from '../../../../api/generated/models/artifactReport'
import type { BaseModel } from '../../types/ReportTypes'
import type { AnyReport } from '../../comp_reusable/report_page_universal/stores/baseReportStore'
import type {
  SolutionReportSlice,
  SolutionReportStore,
  SaveResult,
} from '../../comp_reusable/solution_reports/types/solutionReportStoreTypes'
import {
  saveNewModelAndReport,
  updateModel,
  saveNewReport,
  updateReport,
  replaceArtifact,
} from '../../../../api_operations/saveReportOrAndModel'

import { hasChanges } from '../../../../utils/ModelComparer'
import washReportDocument from '../../../../utils/HackyExecutiveSummaryWashing'
import formatIndependentDriversForModelAPI from '../../../../utils/formatIndependentDriversForModelAPI'

// ---- Optimize Report Implementation ----
export const createOptimizeReportSlice: StateCreator<
  OptimizeReportStore,
  [['zustand/immer', never]],
  [],
  SolutionReportSlice
> = (set, get) => ({
  hasModelBeenSaved: false,
  savingState: { model: false, report: false },
  crntOldModelData: null,
  freshArtifactId: null,

  // Component data initial state
  initialModelData: null,
  mode: null,
  oldModelData: null,

  // IDs for replace operation in EDIT_MODEL (explore mode)
  oldArtifactId: null,
  newArtifactId: null,

  // Reset component data
  resetAllStoreData: () => {
    // Reset common fields using base store function
    get().resetCommonStoreData()

    // Reset optimize-specific state
    set((state) => {
      state.initialModelData = null
      state.mode = null
      state.oldModelData = null
      state.hasModelBeenSaved = false
      state.savingState = { model: false, report: false }
      state.crntOldModelData = null
      state.freshArtifactId = null
      state.oldArtifactId = null
      state.newArtifactId = null
    })
  },

  // Convenience function to initialize all component data at once
  initializeNavigateData: (params: {
    initialModelData?: BaseModel
    mode?: string
    routedReportId?: number
    oldModelData?: BaseModel
  }) =>
    set((state) => {
      if (params.initialModelData !== undefined) {
        state.initialModelData = params.initialModelData as any
      }
      if (params.mode !== undefined) {
        state.mode = params.mode
      }
      if (params.routedReportId !== undefined) {
        state.routedReportId = params.routedReportId
      }
      if (params.oldModelData !== undefined) {
        state.oldModelData = params.oldModelData as any
      }
      // Note: anyReport is handled by the base slice through the initialize function
    }),

  // Comprehensive initialization function that handles all component setup
  initializeFromNavigation: async (params: {
    data?: AnyReport
    mode?: string
    modelData?: BaseModel
    oldModelData?: BaseModel
    routedReportId?: string
    pushToast?: any
    oldArtifactId?: number
    newArtifactId?: number
  }) => {
    const {
      data,
      mode: routeMode,
      modelData,
      oldModelData,
      routedReportId,
      pushToast,
      oldArtifactId,
      newArtifactId,
    } = params

    // Step 1: Determine the operational mode
    const determineMode = () => {
      if (routeMode) return routeMode
      if (routedReportId) return 'VIEW_REPORT'
      throw new Error(
        'No valid mode provided. Ensure routeMode or routedReportId is set.',
      )
    }

    const viewingMode = determineMode()
    // Convert string ID from URL params to number for store (API expects number)
    const routedReportIdNum = routedReportId
      ? parseInt(routedReportId, 10)
      : undefined

    // Step 3: Initialize component data
    set((state) => {
      state.initialModelData = (modelData as any) || null
      state.mode = viewingMode
      state.routedReportId = routedReportIdNum
      state.oldModelData = (oldModelData as any) || null
      state.crntOldModelData = (oldModelData as any) || null
      // Store artifact IDs for replace operation in EDIT_MODEL
      state.oldArtifactId = oldArtifactId || null
      state.newArtifactId = newArtifactId || null
    })

    // Step 4: Set unsaved changes state based on mode
    get().setHasUnsavedChanges(viewingMode !== 'VIEW_REPORT')

    // Step 5: Load report data based on mode
    if (viewingMode === 'VIEW_REPORT') {
      // Fetch report from server using base store utility
      if (!routedReportId) return
      await get().fetchReportFromServer(
        '/api/artifacts/reports',
        routedReportId,
        pushToast,
      )
    } else if (data) {
      // Use data passed through navigation state
      get().initialize(data)
    }
  },

  // API operations - split export into data fetching part
  exportReportData: async (
    format: string,
    onProgress?: (progress: number) => void,
  ) => {
    const state = get()
    const title = state.getTitle()
    const anyReport = state.anyReport
    const reportDocument = state.getReportDocument()
    const hasAIPlus = state.getAIPlus()

    if (!anyReport || !reportDocument) {
      return { success: false, error: 'No report data available' }
    }

    try {
      const washedReportDocument = hasAIPlus
        ? washReportDocument(reportDocument)
        : reportDocument

      const exportPayload = {
        title: title,
        user: state.getUserName(),
        fit_score: anyReport.model_fit_score,
        health_score: anyReport.model_health_score,
        executive_summary: washedReportDocument,
        // @ts-ignore
        key_indicators: anyReport.key_indicators,
      }

      if (anyReport.artifact) {
        const artifact = anyReport.artifact as any
        if (
          artifact.control_drivers &&
          Array.isArray(artifact.control_drivers) &&
          artifact.control_drivers.length > 0
        ) {
          const formattedDrivers = formatIndependentDriversForModelAPI(
            artifact.control_drivers,
          )
          if (formattedDrivers) {
            // @ts-ignore
            exportPayload.control_drivers = formattedDrivers
          }
        }
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/artifacts/export?q=${format}&s=optimized`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(exportPayload),
        },
      )

      if (!response.ok) {
        return {
          success: false,
          error: `Failed to download: ${response.status}`,
        }
      }

      // Use base store utility for stream processing
      const blob = await get().processDownloadStream(response, onProgress)
      return {
        success: true,
        blob,
        filename: `${title}.${format}`,
      }
    } catch (error) {
      return { success: false, error: (error as Error).message }
    }
  },

  handleExport: async (formats, params) => {
    const state = get()
    // Use base store implementation with optimize-specific export data function
    await state.baseHandleExport(formats, {
      ...params,
      exportReportData: state.exportReportData,
    })
  },

  handleSaveModelAndReportBtn: async (pushToast) => {
    const state = get()

    // Get data from store instead of parameters
    const mode = state.mode
    const initialModelData = state.initialModelData

    set((state) => {
      state.savingState = { model: true, report: true }
    })

    let result: SaveResult = { modelSuccess: false, reportSuccess: false }

    try {
      if (mode === 'NEW_MODEL') {
        result = await state.handleSaveNewModelAndNewReport()
      } else if (mode === 'EDIT_MODEL') {
        if (!initialModelData) {
          throw new Error('Initial model data is required for EDIT_MODEL mode')
        }
        result = await state.handleSaveCrntModelAndNewReport()
      } else {
        console.error('Invalid mode for saving model and report:', mode)
        ;(pushToast as any)(
          React.createElement(
            Message,
            { type: 'error' },
            'Error: Invalid mode for saving model and report',
          ),
          { placement: 'topCenter', duration: 5000 },
        )
        return
      }

      if (result.modelSuccess && result.reportSuccess) {
        ;(pushToast as any)(
          React.createElement(
            Message,
            { type: 'success' },
            'Model and report saved successfully',
          ),
          { placement: 'topCenter', duration: 3000 },
        )
      } else if (result.modelSuccess) {
        ;(pushToast as any)(
          React.createElement(
            Message,
            { type: 'warning' },
            'Model saved successfully, but report not saved',
          ),
          { placement: 'topCenter', duration: 3000 },
        )
      } else if (result.reportSuccess) {
        ;(pushToast as any)(
          React.createElement(
            Message,
            { type: 'warning' },
            'Report saved successfully, but model not saved',
          ),
          { placement: 'topCenter', duration: 3000 },
        )
      } else {
        ;(pushToast as any)(
          React.createElement(
            Message,
            { type: 'error' },
            'Model and report not saved',
          ),
          { placement: 'topCenter', duration: 3000 },
        )
      }
    } finally {
      set((state) => {
        state.savingState = { model: false, report: false }
      })
    }
  },

  handleUpdateModelBtn: async (pushToast) => {
    const state = get()

    // Get data from store instead of parameters
    const initialModelData = state.initialModelData

    set((state) => {
      state.savingState.model = true
    })

    try {
      const success = await state.handleUpdateModel(
        initialModelData || undefined,
      )

      if (success) {
        ;(pushToast as any)(
          React.createElement(
            Message,
            { type: 'success' },
            'Model updated successfully',
          ),
          { placement: 'topCenter', duration: 3000 },
        )
      } else {
        ;(pushToast as any)(
          React.createElement(
            Message,
            { type: 'error' },
            'Error: Failed to update model',
          ),
          { placement: 'topCenter', duration: 5000 },
        )
      }
    } finally {
      set((state) => {
        state.savingState.model = false
      })
    }
  },

  handleSaveReportBtn: async (pushToast) => {
    const state = get()

    // Use base store template with optimize-specific save functions
    await state.handleSaveReportTemplate({
      savingStateKey: 'report',
      updateFunction: (reportId: number) => state.handleUpdateReport(reportId),
      saveNewFunction: () => state.handleSaveNewReportWithModelKnown(),
      pushToast,
      setSavingState: (key, value) => {
        set((stateObj) => {
          if (key) {
            ;(stateObj.savingState as any)[key] = value
          }
        })
      },
    })
  },

  handleUpdateModel: async (initialModelData?: BaseModel) => {
    const state = get()

    // Use parameter if provided, otherwise get from store
    const modelData = initialModelData || state.initialModelData

    if (!modelData) {
      console.error('No model data available for update')
      return false
    }

    try {
      const result = await updateModel(
        (modelData as any).id,
        modelData as any,
        'optimize',
      )

      if (result.success) {
        set((state) => {
          state.crntOldModelData = { ...modelData } as any
          state.hasModelBeenSaved = true
        })
      }

      return result.success
    } catch (error) {
      console.error('Error updating model:', error)
      return false
    }
  },

  handleUpdateReport: async (reportId: number) => {
    const state = get()

    // Get all data from store
    const title = state.getTitle()
    const reportDocument = state.getReportDocument()
    const initialModelData = state.initialModelData

    if (!state.anyReport) {
      console.error('No report data available for update')
      return false
    }

    const reportData: any = {
      ...state.anyReport,
      name: title,
      executive_summary: reportDocument,
      project_solution_id: state.anyReport.project_solution?.id,
      client_id: state.anyReport.client?.id,
    }

    // Handle multiple ways to get artifact ID
    if (
      state.anyReport?.artifact?.id ||
      (initialModelData as any)?.artifact_id ||
      initialModelData?.id ||
      state.freshArtifactId
    ) {
      reportData.artifact_id =
        state.anyReport?.artifact?.id ||
        (initialModelData as any)?.artifact_id ||
        initialModelData?.id ||
        state.freshArtifactId
    }

    const success = await updateReport(reportId, reportData)

    if (success) {
      get().setHasUnsavedChanges(false)
    }

    return success
  },

  handleSaveNewReportWithModelKnown: async () => {
    const state = get()

    // Get all data from store
    const title = state.getTitle()
    const reportDocument = state.getReportDocument()
    const initialModelData = state.initialModelData

    if (!state.anyReport) {
      console.error('No report data available for saving')
      return false
    }

    const reportData: any = {
      ...state.anyReport,
      name: title,
      executive_summary: reportDocument,
      project_solution_id: state.anyReport.project_solution?.id,
      client_id: state.anyReport.client?.id,
    }

    // Handle multiple ways to get artifact ID
    if (
      state.anyReport?.artifact?.id ||
      (initialModelData as any)?.artifact_id ||
      initialModelData?.id ||
      state.freshArtifactId
    ) {
      reportData.artifact_id =
        state.anyReport?.artifact?.id ||
        (initialModelData as any)?.artifact_id ||
        initialModelData?.id ||
        state.freshArtifactId
    } else {
      throw new Error('No artifact ID found in initial model data.')
    }

    const result = await saveNewReport(reportData)

    if (result.success) {
      const isNewIdAssigned =
        !state.freshReportId && result.data && result.data.id

      if (isNewIdAssigned) {
        set((state) => {
          state.freshReportId = result.data.id
        })
      }

      get().setHasUnsavedChanges(false)

      return true
    }

    return false
  },

  handleSaveNewModelAndNewReport: async () => {
    const state = get()

    // Get all data from store
    const title = state.getTitle()
    const reportDocument = state.getReportDocument()

    if (!state.anyReport) {
      console.error('No report data available for saving')
      return { modelSuccess: false, reportSuccess: false }
    }

    try {
      // @ts-ignore
      const reportData: Partial<ArtifactReport> = {
        ...state.anyReport,
        name: title,
        executive_summary: reportDocument,
        // Use _id field if available (new API), otherwise extract from nested object (legacy)
        artifact_id:
          (state.anyReport as any).artifact_id || state.anyReport.artifact?.id,
        project_solution_id:
          (state.anyReport as any).project_solution_id ||
          state.anyReport.project_solution?.id,
        client_id:
          (state.anyReport as any).client_id || state.anyReport.client?.id,
      }

      const result = await saveNewModelAndReport(
        reportData,
        title || 'Untitled Report',
      )

      if (result && result.success) {
        if (result.data?.report?.id && result.data?.model?.id) {
          set((state) => {
            state.freshReportId = result.data!.report.id
            state.freshArtifactId = result.data!.model.id || null
            state.hasModelBeenSaved = true
          })
          get().setHasUnsavedChanges(false)
        }
      }

      return { modelSuccess: result.success, reportSuccess: result.success }
    } catch (error) {
      console.error('Error saving model and report:', error)
      return { modelSuccess: false, reportSuccess: false }
    }
  },

  handleSaveCrntModelAndNewReport: async () => {
    const state = get()

    // Get IDs for replace operation from store
    const oldArtifactId = state.oldArtifactId
    const newArtifactId = state.newArtifactId

    // Use new replace endpoint if we have both IDs (explore mode)
    if (oldArtifactId && newArtifactId) {
      const replaceResult = await replaceArtifact(
        oldArtifactId,
        newArtifactId,
        'optimize',
      )

      if (replaceResult.success) {
        // Update store: use oldArtifactId since the temp variant was deleted
        // Also update anyReport.artifact.id so report saves with correct artifact
        set((stateObj) => {
          stateObj.freshArtifactId = oldArtifactId
          stateObj.hasModelBeenSaved = true
          stateObj.crntOldModelData = { ...state.initialModelData } as any
          // Update artifact ID in report data to point to the original (now updated) artifact
          if (stateObj.anyReport?.artifact) {
            stateObj.anyReport.artifact.id = oldArtifactId
          }
        })

        // Now save the report linked to the replaced artifact
        const reportSuccess = await state.handleSaveNewReportWithModelKnown()
        return { modelSuccess: true, reportSuccess }
      }

      return { modelSuccess: false, reportSuccess: false }
    }

    // Fallback to old behavior if IDs not available (shouldn't happen in normal flow)
    const initialModelData = state.initialModelData
    const modelSuccess = await state.handleUpdateModel(
      initialModelData || undefined,
    )
    const reportSuccess = await state.handleSaveNewReportWithModelKnown()

    return { modelSuccess, reportSuccess }
  },

  // Getter functions
  getModelHasChanges: () => {
    const state = get()
    return state.crntOldModelData && state.initialModelData
      ? hasChanges(state.initialModelData as any, state.crntOldModelData as any)
      : false
  },
})

// ---- Combined store type ----
export type OptimizeReportStore = SolutionReportStore

export const useOptimizeReportStore = create<OptimizeReportStore>()(
  subscribeWithSelector(
    immer((set, get, api) => {
      const baseSlice = createBaseSlice(set as any, get as any, api as any)
      const optimizeSlice = createOptimizeReportSlice(set, get, api)

      return {
        ...baseSlice,
        ...optimizeSlice,
      }
    }),
  ),
)
